import React from 'react';
import { List, Tag, Typography, Space, Empty, Collapse } from 'antd';
import { LotteryTicket, EngagementTicketStatus, EventWithTickets } from '../../types/engagement.types';

const { Text } = Typography;
const { Panel } = Collapse;

const STATUS_COLORS: Record<EngagementTicketStatus, string> = {
  VALID: 'success',
  PENDING: 'warning',
  INVALID: 'error',
};

const STATUS_LABELS: Record<EngagementTicketStatus, string> = {
  VALID: 'Válido',
  PENDING: 'Pendente',
  INVALID: 'Inválido',
};

interface LotteryTicketListProps {
  events: EventWithTickets[];
  minimumReached: boolean;
  minimumPrizePoints?: number;
  totalPoints?: number;
}

export const LotteryTicketList: React.FC<LotteryTicketListProps> = ({
  events,
  minimumReached,
  minimumPrizePoints,
  totalPoints,
}) => {
  const formatTicketDisplay = (ticket: LotteryTicket): string => {
    if (ticket.ticketDay && ticket.ticketNumber) {
      return `${ticket.ticketDay}-${ticket.ticketNumber}`;
    }
    return ticket.displayId;
  };

  const allTickets = events.flatMap((e) => e.tickets);
  const validCount = allTickets.filter((t) => t.status === 'VALID').length;
  const pendingCount = allTickets.filter((t) => t.status === 'PENDING').length;

  if (events.length === 0) {
    return (
      <Empty
        description="Nenhum bilhete gerado neste ciclo"
        image={Empty.PRESENTED_IMAGE_SIMPLE}
      />
    );
  }

  return (
    <div>
      <Space wrap style={{ marginBottom: 16 }}>
        <Text>Total de bilhetes: <strong>{allTickets.length}</strong></Text>
        {validCount > 0 && <Tag color="success">{validCount} válidos</Tag>}
        {pendingCount > 0 && (
          <Tag color="warning">
            {pendingCount} pendentes
            {minimumPrizePoints != null && totalPoints != null && !minimumReached && (
              <> (faltam {minimumPrizePoints - totalPoints} pt)</>
            )}
          </Tag>
        )}
        {minimumReached && (
          <Tag color="green">✓ Elegível para sorteio</Tag>
        )}
      </Space>

      <Collapse size="small" accordion={false}>
        {events.map((event) => (
          <Panel
            key={event.eventId}
            header={
              <Space>
                <Text strong>+{event.pointsDelta} pts</Text>
                <Text type="secondary">{new Date(event.happenedAt).toLocaleDateString('pt-BR')}</Text>
                <Tag>{event.tickets.length} bilhete{event.tickets.length !== 1 ? 's' : ''}</Tag>
              </Space>
            }
          >
            <List
              size="small"
              dataSource={event.tickets}
              renderItem={(ticket: LotteryTicket) => (
                <List.Item
                  extra={
                    <Tag color={STATUS_COLORS[ticket.status]}>
                      {STATUS_LABELS[ticket.status]}
                    </Tag>
                  }
                >
                  <Text code>{formatTicketDisplay(ticket)}</Text>
                </List.Item>
              )}
            />
          </Panel>
        ))}
      </Collapse>
    </div>
  );
};

export default LotteryTicketList;
